const UNAUTHORIZED_STATUS = 401;

export const UNAUTHORIZED_API_LOGOUT_THRESHOLD = 2;
export const UNAUTHORIZED_API_LOGOUT_WINDOW_MS = 10_000;

type UnauthorizedApiLogoutHandler = () => void | Promise<void>;
type ApiErrorLike = {
  message?: unknown;
  response?: {
    data?: unknown;
    status?: unknown;
  };
};

let unauthorizedApiLogoutHandler: UnauthorizedApiLogoutHandler | null = null;
let unauthorizedRequestTimestamps: number[] = [];
let unauthorizedApiLogoutTriggered = false;

const WRONG_AUTHENTICATION_TOKEN_PATTERN = /wrong\s+authentication\s+token/i;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const hasStoredAccessToken = () => {
  try {
    return !!window.localStorage.getItem('accessToken');
  } catch {
    return false;
  }
};

const getResponseStatus = (error: unknown) => {
  if (!isRecord(error)) return undefined;

  const { response } = error as ApiErrorLike;
  const status = response?.status;

  return typeof status === 'number' ? status : Number(status);
};

export const containsWrongAuthenticationTokenMessage = (
  value: unknown,
  seen = new WeakSet<object>()
): boolean => {
  if (typeof value === 'string') {
    return WRONG_AUTHENTICATION_TOKEN_PATTERN.test(value);
  }

  if (Array.isArray(value)) {
    if (seen.has(value)) return false;

    seen.add(value);

    return value.some((item) =>
      containsWrongAuthenticationTokenMessage(item, seen)
    );
  }

  if (!isRecord(value)) return false;
  if (seen.has(value)) return false;

  seen.add(value);

  return Object.values(value).some((item) =>
    containsWrongAuthenticationTokenMessage(item, seen)
  );
};

const hasWrongAuthenticationTokenMessage = (error: unknown) => {
  if (!isRecord(error)) return false;

  const apiError = error as ApiErrorLike;

  return (
    containsWrongAuthenticationTokenMessage(apiError.message) ||
    containsWrongAuthenticationTokenMessage(apiError.response?.data)
  );
};

const triggerUnauthorizedApiLogout = () => {
  if (unauthorizedApiLogoutTriggered || !unauthorizedApiLogoutHandler) {
    return false;
  }

  unauthorizedApiLogoutTriggered = true;

  void Promise.resolve(unauthorizedApiLogoutHandler()).catch((error) => {
    console.error('Unable to logout after unauthorized API responses', error);
  });

  return true;
};

export const registerUnauthorizedApiLogoutHandler = (
  handler: UnauthorizedApiLogoutHandler
) => {
  unauthorizedApiLogoutHandler = handler;

  return () => {
    if (unauthorizedApiLogoutHandler === handler) {
      unauthorizedApiLogoutHandler = null;
    }
  };
};

export const resetUnauthorizedApiRequestTracking = () => {
  unauthorizedRequestTimestamps = [];
  unauthorizedApiLogoutTriggered = false;
};

export const maybeTriggerUnauthorizedApiLogout = (error: unknown) => {
  if (!hasStoredAccessToken()) return false;

  if (hasWrongAuthenticationTokenMessage(error)) {
    return triggerUnauthorizedApiLogout();
  }

  if (getResponseStatus(error) !== UNAUTHORIZED_STATUS) {
    return false;
  }

  const now = Date.now();
  unauthorizedRequestTimestamps = unauthorizedRequestTimestamps.filter(
    (timestamp) => now - timestamp <= UNAUTHORIZED_API_LOGOUT_WINDOW_MS
  );
  unauthorizedRequestTimestamps.push(now);

  if (
    unauthorizedRequestTimestamps.length >= UNAUTHORIZED_API_LOGOUT_THRESHOLD
  ) {
    return triggerUnauthorizedApiLogout();
  }

  return false;
};
